/**
 * Plano do radar: para cada quadro do loop, qual varredura de cada radar
 * aparece (e a URL dela) — função pura, testada em `plano.test.ts`.
 */
import { BASE_RADAR, carimbo, type Manifesto } from './api';
import type { Produto } from './produtos';
import { casarQuadros, TOLERANCIA_MS } from './tempo';

export interface PlanoRadarItem {
  id: string;
  /** Chave no cache (radar + produto). */
  chave: string;
  /** Varreduras publicadas deste radar/produto (epoch ms, crescente). */
  varreduras: number[];
  /** Varredura exibida em cada quadro (null = radar some naquele quadro). */
  tempos: (number | null)[];
  /** URL (chave do cache) de cada quadro. */
  urls: (string | null)[];
  /** URL (chave do cache) de qualquer varredura deste radar: `urlDe(t)`. */
  prefixo: string;
  sufixo: string;
}

/** URL (chave do cache) da varredura `t` de um radar do plano. */
export function urlDe(item: Pick<PlanoRadarItem, 'prefixo' | 'sufixo'>, t: number): string {
  return item.prefixo + carimbo(t) + item.sufixo;
}

export interface PlanoRadar {
  produto: Produto;
  quadros: number[];
  radares: PlanoRadarItem[];
}

/**
 * `versao(id)` entra na URL como fragmento (`#g…`): o fragmento não vai ao
 * servidor, mas separa no cache os bitmaps reprojetados com geometrias
 * diferentes (se o SIPAM corrigir a extensão de um radar, nada da geometria
 * velha é reaproveitado).
 */
export function montarPlano(
  quadros: readonly number[],
  manifesto: Manifesto | null,
  produto: Produto,
  versao: (id: string) => string = () => '',
  base: string = BASE_RADAR,
): PlanoRadar {
  const radares: PlanoRadarItem[] = [];
  for (const r of manifesto?.radares ?? []) {
    const varreduras = r.produtos[produto]?.t ?? [];
    const tempos = casarQuadros(quadros, varreduras, TOLERANCIA_MS);
    const v = versao(r.id);
    const item = { prefixo: `${base}/${r.id}/${produto}/`, sufixo: `.png${v ? `#${v}` : ''}` };
    const urls = tempos.map((t) => (t === null ? null : urlDe(item, t)));
    radares.push({ id: r.id, chave: `${r.id}:${produto}`, varreduras, tempos, urls, ...item });
  }
  return { produto, quadros: [...quadros], radares };
}

/** URLs distintas do plano (para contar o progresso). */
export function urlsDoPlano(plano: PlanoRadar | null): string[] {
  const vistas = new Set<string>();
  for (const r of plano?.radares ?? []) for (const u of r.urls) if (u) vistas.add(u);
  return [...vistas];
}
